import { useEffect, useState } from 'react'
import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { loginRequest } from '../../configs'
import { AuthContext } from '../../hooks/useAuth'
import type { AuthContextValue } from '../../hooks/useAuth'
import { getToken } from './helpers/getToken'
import type { MsalBridgeProps } from './MsalBridge.types'

export function MsalBridge({ children }: MsalBridgeProps) {
  const { instance, accounts, inProgress } = useMsal()
  const account = accounts[0] ?? null
  const [ssoChecked, setSsoChecked] = useState(accounts.length > 0)

  useEffect(() => {
    if (ssoChecked || inProgress !== InteractionStatus.None) {
      return
    }

    // A prior role check failed and sent us here via logoutRedirect — retrying
    // ssoSilent would immediately re-authenticate the same account and loop.
    const params = new URLSearchParams(window.location.search)
    if (params.get('error') === 'unauthorized') {
      setSsoChecked(true)
      return
    }

    instance
      .ssoSilent(loginRequest)
      .catch(() => {
        // No existing Microsoft session (or silent SSO is blocked) — the user
        // will see the login screen and can sign in interactively instead.
      })
      .finally(() => setSsoChecked(true))
  }, [ssoChecked, inProgress, instance])

  const value: AuthContextValue = {
    isAuthenticated: accounts.length > 0,
    account,
    getToken: () => getToken(instance, account),
    login: () => void instance.loginRedirect(loginRequest),
    logout: () =>
      void instance.logoutRedirect({
        postLogoutRedirectUri: `${window.location.origin}?error=unauthorized`,
      }),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
